import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, Activity, Loader2, AlertCircle, Settings, Trash2, Edit, X, Save,
  Shield, Calendar, MapPin, User, Upload, ImageIcon, Plus, Mail, Send,
} from 'lucide-react';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Player, Assessment } from '../types';
import { teamService } from '../services/teamService';
import { assessmentService } from '../services/assessmentService';
import { api } from '../services/api';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Legend,
} from 'recharts';
import { NATIONALITIES, getFlagForNationality, getNameForNationality } from '../constants/nationalities';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { DevelopmentFocusAreas } from '../components/DevelopmentFocusAreas';

const CORNERS = ['technical', 'physical', 'psychological', 'social'] as const;
type Corner = (typeof CORNERS)[number];

const CORNER_META: Record<Corner, { label: string; color: string; chartColor: string; bgClass: string }> = {
  technical: { label: 'Technical', color: 'red', chartColor: '#9333ea', bgClass: 'bg-red-500' },
  physical: { label: 'Physical', color: 'yellow', chartColor: '#ea580c', bgClass: 'bg-yellow-500' },
  psychological: { label: 'Psychological', color: 'green', chartColor: '#3b82f6', bgClass: 'bg-green-500' },
  social: { label: 'Social', color: 'blue', chartColor: '#16a34a', bgClass: 'bg-blue-500' },
};

function calculateAge(dob: Date | string): number {
  const today = new Date(); const birth = new Date(dob);
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
}

function formatDate(d: Date | string): string {
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function cornerAvg(scores: Record<string, number | undefined>): number {
  const vals = Object.values(scores).filter((v): v is number => v !== undefined && v !== null);
  if (vals.length === 0) return 0;
  return parseFloat((vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1));
}

/* ── Circular Progress ─────────────────────────────────────────────────── */
function CircularProgress({ value, label, color = 'green', size = 'sm', max = 100 }: { value: number; label?: string; color?: string; size?: 'sm' | 'lg'; max?: number }) {
  const pct = (value / max) * 100;
  const isLarge = size === 'lg';
  const radius = isLarge ? 52 : 36;
  const svgSize = isLarge ? 120 : 80;
  const circ = 2 * Math.PI * radius;
  const offset = circ - (pct / 100) * circ;
  const strokeW = isLarge ? 8 : 6;
  const map: Record<string, { stroke: string; text: string; bg: string }> = {
    green: { stroke: '#16a34a', text: 'text-green-600', bg: 'stroke-green-100' },
    blue: { stroke: '#3b82f6', text: 'text-blue-600', bg: 'stroke-blue-100' },
    red: { stroke: '#dc2626', text: 'text-red-600', bg: 'stroke-red-100' },
    yellow: { stroke: '#ca8a04', text: 'text-yellow-600', bg: 'stroke-yellow-100' },
  };
  const c = map[color] || map.green;
  return (
    <div className="flex flex-col items-center">
      <div className="relative" style={{ width: svgSize, height: svgSize }}>
        <svg className="transform -rotate-90" style={{ width: svgSize, height: svgSize }}>
          <circle cx={svgSize / 2} cy={svgSize / 2} r={radius} className={c.bg} strokeWidth={strokeW} fill="none" />
          <circle cx={svgSize / 2} cy={svgSize / 2} r={radius} stroke={c.stroke} strokeWidth={strokeW} fill="none"
            strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round" className="transition-all duration-500" />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className={`${isLarge ? 'text-3xl' : 'text-xl'} font-bold ${c.text}`}>{Number.isInteger(value) ? value : value.toFixed(1)}</span>
        </div>
      </div>
      {label && <span className="text-xs font-medium text-gray-700 mt-2 text-center">{label}</span>}
    </div>
  );
}

/* ── Crop to head/shoulders and compress ────────────────────────────────── */
function compressImage(blob: Blob, maxSize: number, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const detect = document.createElement('canvas');
      detect.width = img.width;
      detect.height = img.height;
      const dCtx = detect.getContext('2d');
      if (!dCtx) { resolve(blob); return; }
      dCtx.drawImage(img, 0, 0);
      const data = dCtx.getImageData(0, 0, img.width, img.height).data;

      // Check if image has transparency (bg was removed)
      let hasTransparency = false;
      let minY = img.height, maxY = 0, minX = img.width, maxX = 0;
      for (let y = 0; y < img.height; y++) {
        for (let x = 0; x < img.width; x++) {
          const alpha = data[(y * img.width + x) * 4 + 3];
          if (alpha < 200) { hasTransparency = true; }
          if (alpha > 30) {
            if (y < minY) minY = y;
            if (y > maxY) maxY = y;
            if (x < minX) minX = x;
            if (x > maxX) maxX = x;
          }
        }
      }

      let cropTop: number, cropH: number, cropLeft: number, cropW: number;

      if (hasTransparency && maxY > minY && maxX > minX) {
        // Background was removed — smart crop around the person
        const contentH = maxY - minY;
        const contentW = maxX - minX;
        cropTop = Math.max(0, minY - Math.round(contentH * 0.02));
        const cropBottom = Math.min(img.height, minY + Math.round(contentH * 0.55));
        cropH = cropBottom - cropTop;
        const personCenterX = minX + contentW / 2;
        cropW = Math.round(cropH * 0.8);
        cropLeft = Math.max(0, Math.min(img.width - cropW, Math.round(personCenterX - cropW / 2)));
      } else {
        // No transparency — simple top-center crop (head and shoulders)
        cropH = Math.round(img.height * 0.55);
        cropTop = 0;
        cropW = Math.round(cropH * 0.8);
        cropLeft = Math.max(0, Math.round((img.width - cropW) / 2));
      }

      const targetW = maxSize;
      const targetH = Math.round(maxSize * 1.25);
      const canvas = document.createElement('canvas');
      canvas.width = targetW;
      canvas.height = targetH;
      const ctx = canvas.getContext('2d');
      if (!ctx) { resolve(blob); return; }
      ctx.drawImage(img, cropLeft, cropTop, cropW, cropH, 0, 0, targetW, targetH);

      canvas.toBlob(
        (result) => resolve(result || blob),
        'image/webp',
        quality
      );
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(blob);
  });
}

/* ── Main Component ────────────────────────────────────────────────────── */
export const PlayerProfilePage: React.FC = () => {
  const { playerId } = useParams<{ playerId: string }>();
  const navigate = useNavigate();
  const [player, setPlayer] = useState<Player | null>(null);
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [teamLogoUrl, setTeamLogoUrl] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [error, setError] = useState('');

  // Edit state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFirstName, setEditFirstName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [editDob, setEditDob] = useState('');
  const [editPosition, setEditPosition] = useState('');
  const [editFanNumber, setEditFanNumber] = useState('');
  const [editNationality, setEditNationality] = useState('');
  const [editPhotoFile, setEditPhotoFile] = useState<File | null>(null);
  const [editPhotoPreview, setEditPhotoPreview] = useState('');
  const [removingBg, setRemovingBg] = useState(false);
  const [bgProgress, setBgProgress] = useState(0);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [editTab, setEditTab] = useState<'details' | 'contacts'>('details');
  const [editContactEmail, setEditContactEmail] = useState('');
  const [editGuardians, setEditGuardians] = useState<{ firstName: string; lastName: string; email: string }[]>([]);

  useEffect(() => {
    if (!playerId) return;
    const load = async () => {
      try {
        setLoading(true);
        const p = await teamService.getPlayer(playerId);
        setPlayer(p);
        // Fetch team logo
        try {
          const team = await teamService.getTeam(p.teamId);
          setTeamLogoUrl(team.logoUrl);
        } catch { /* ignore */ }
        try {
          const all = await assessmentService.getPlayerAssessments(playerId);
          setAssessments(all.filter((a) => a.isComplete).sort(
            (a, b) => new Date(a.assessmentDate).getTime() - new Date(b.assessmentDate).getTime()
          ));
        } catch { setAssessments([]); }
      } catch (err: any) {
        setError(err.response?.data?.error?.message || 'Failed to load player');
      } finally { setLoading(false); }
    };
    load();
  }, [playerId]);

  const latestAssessment = assessments.length > 0 ? assessments[assessments.length - 1] : null;

  const cornerScores = useMemo(() => {
    if (!latestAssessment) return null;
    return CORNERS.reduce((acc, c) => {
      acc[c] = cornerAvg(latestAssessment.scores[c] as Record<string, number | undefined>);
      return acc;
    }, {} as Record<Corner, number>);
  }, [latestAssessment]);

  const overallRating = useMemo(() => {
    if (!cornerScores) return 0;
    const avg = CORNERS.reduce((s, c) => s + cornerScores[c], 0) / CORNERS.length;
    return Math.round(avg * 10);
  }, [cornerScores]);

  // Per-skill data from latest assessment
  const skillCategories = useMemo(() => {
    if (!latestAssessment) return [];
    const attrNames: Record<string, string> = {
      ball_control: 'Ball Control', passing_accuracy: 'Passing Accuracy', positioning: 'Positioning',
      tactical_awareness: 'Tactical Awareness', shooting: 'Shooting', dribbling: 'Dribbling',
      speed: 'Speed', endurance: 'Endurance', strength: 'Strength', agility: 'Agility',
      coordination: 'Coordination', balance: 'Balance', confidence: 'Confidence', resilience: 'Resilience',
      decision_making: 'Decision Making', concentration: 'Concentration', motivation: 'Motivation',
      composure: 'Composure', teamwork: 'Teamwork', communication: 'Communication', respect: 'Respect',
      leadership: 'Leadership', coachability: 'Coachability', sportsmanship: 'Sportsmanship',
    };
    return CORNERS.map((c) => {
      const scores = latestAssessment.scores[c] as Record<string, number | undefined>;
      return {
        corner: c,
        meta: CORNER_META[c],
        avg: cornerScores ? Math.round(cornerScores[c] * 10) : 0,
        skills: Object.entries(scores)
          .filter(([, v]) => v !== undefined && v !== null)
          .map(([k, v]) => ({ name: attrNames[k] || k, score: v as number })),
      };
    });
  }, [latestAssessment, cornerScores]);

  const progressData = useMemo(() => {
    return assessments.map((a) => ({
      date: new Date(a.assessmentDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }),
      technical: cornerAvg(a.scores.technical as Record<string, number | undefined>),
      physical: cornerAvg(a.scores.physical as Record<string, number | undefined>),
      psychological: cornerAvg(a.scores.psychological as Record<string, number | undefined>),
      social: cornerAvg(a.scores.social as Record<string, number | undefined>),
    }));
  }, [assessments]);

  const radarData = useMemo(() => {
    if (!cornerScores) return [];
    return CORNERS.map((c) => ({ skill: CORNER_META[c].label, value: cornerScores[c] }));
  }, [cornerScores]);

  const handlePhotoSelect = async (file: File) => {
    if (file.size > 5 * 1024 * 1024) {
      setEditError('Photo must be under 5MB');
      return;
    }
    setRemovingBg(true);
    setBgProgress(10);
    setEditError('');
    try {
      const { removeBackground } = await import('@imgly/background-removal');
      setBgProgress(30);
      const blob = await removeBackground(file, {
        output: { format: 'image/png' },
        progress: (key: string, current: number, total: number) => {
          if (total > 0) setBgProgress(30 + Math.round((current / total) * 60));
        },
      });
      setBgProgress(95);

      // Resize and compress to fit within Lambda payload limits
      const compressed = await compressImage(blob, 600, 0.85);
      const resultFile = new File([compressed], 'player-photo.webp', { type: 'image/webp' });
      setEditPhotoFile(resultFile);
      setEditPhotoPreview(URL.createObjectURL(resultFile));
      setBgProgress(100);
    } catch (err) {
      console.error('Background removal failed, using original:', err);
      const compressed = await compressImage(file, 600, 0.85);
      const resultFile = new File([compressed], 'player-photo.webp', { type: 'image/webp' });
      setEditPhotoFile(resultFile);
      setEditPhotoPreview(URL.createObjectURL(resultFile));
      setBgProgress(100);
    } finally {
      setRemovingBg(false);
      setTimeout(() => setBgProgress(0), 500);
    }
  };

  const openEditModal = () => {
    if (!player) return;
    setEditFirstName(player.firstName); setEditLastName(player.lastName);
    setEditDob(new Date(player.dateOfBirth).toISOString().split('T')[0]);
    setEditPosition(player.position || ''); setEditFanNumber(player.fanNumber || '');
    setEditNationality(player.nationality || '');
    setEditPhotoFile(null); setEditPhotoPreview('');
    setEditContactEmail((player as any).contactEmail || '');
    setEditGuardians((player as any).guardians || []);
    setEditTab('details');
    setEditError(''); setShowEditModal(true);
  };

  const handleEditSave = async () => {
    if (!player || !playerId) return;
    if (!editFirstName.trim() || !editLastName.trim()) { setEditError('First and last name required'); return; }
    if (!editDob) { setEditError('Date of birth required'); return; }
    if (!editPosition) { setEditError('Position required'); return; }
    setEditSaving(true); setEditError('');
    try {
      const updateData: Record<string, any> = {
        firstName: editFirstName.trim(), lastName: editLastName.trim(),
        dateOfBirth: editDob, position: editPosition, fanNumber: editFanNumber.trim() || undefined,
        nationality: editNationality || undefined,
        contactEmail: editContactEmail.trim() || '',
        guardians: editGuardians.filter(g => g.firstName.trim() && g.email.trim()),
      };

      // If photo file selected, convert to base64
      if (editPhotoFile) {
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result as string;
            resolve(result.split(',')[1] || result);
          };
          reader.onerror = reject;
          reader.readAsDataURL(editPhotoFile);
        });
        updateData.photo = base64;
        updateData.photoContentType = editPhotoFile.type;
      }

      const updated = await teamService.updatePlayer(playerId, updateData);
      setPlayer(updated); setShowEditModal(false);
    } catch (err: any) { setEditError(err.response?.data?.error?.message || 'Failed to update'); }
    finally { setEditSaving(false); }
  };

  const handleDelete = async () => {
    if (!playerId) return;
    if (!window.confirm('Delete this player and all their assessments? This cannot be undone.')) return;
    setDeleting(true);
    try { await teamService.deletePlayer(playerId); navigate('/players'); }
    catch (err: any) { setError(err.response?.data?.error?.message || 'Failed to delete'); setDeleting(false); }
  };

  if (loading) return <LoadingSpinner />;
  if (error || !player) return (
    <div className="p-4 lg:p-8 max-w-4xl mx-auto"><Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>{error || 'Player not found'}</AlertDescription></Alert></div>
  );

  const age = calculateAge(player.dateOfBirth);

  return (
    <div className="p-4 lg:p-8 max-w-7xl mx-auto">
      <Link to="/players" className="inline-flex items-center gap-2 text-sm text-green-600 hover:text-green-700 mb-6 min-h-[44px]">
        <ArrowLeft className="w-4 h-4" />Back to Players
      </Link>

      {/* Hero Section */}
      <div className="bg-gradient-to-br from-green-700 via-green-600 to-green-700 rounded-2xl border border-green-800 p-6 lg:p-8 mb-6 overflow-hidden relative">
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,.1) 10px, rgba(255,255,255,.1) 20px)' }} />
        <div className="relative z-10 flex flex-col lg:flex-row gap-8 items-start">
          {/* Player Card */}
          <div className="flex-shrink-0">
            <div className="relative w-48 h-64 bg-gradient-to-br from-gray-400 to-gray-500 rounded-xl border-4 border-yellow-500 shadow-2xl overflow-hidden">
              {/* Left column: rating, position, flag, logo */}
              <div className="absolute top-2 left-3 z-10 flex flex-col items-center gap-1" style={{ width: '36px' }}>
                <div className="text-4xl font-black leading-none text-yellow-400 drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]">{overallRating}</div>
                <div className="text-[9px] font-semibold uppercase text-white tracking-wide">{player.position || 'Player'}</div>
                <span className="text-xl leading-none mt-0.5">{getFlagForNationality(player.nationality)}</span>
                <div className="w-9 h-9 rounded-full shadow-lg flex items-center justify-center overflow-hidden mt-0.5">
                  {teamLogoUrl ? (
                    <img src={teamLogoUrl} alt="Team logo" className="w-9 h-9 object-contain" />
                  ) : (
                    <div className="w-full h-full bg-white flex items-center justify-center">
                      <Shield className="w-5 h-5 text-green-600" />
                    </div>
                  )}
                </div>
              </div>
              {/* Player photo — shifted right and enlarged */}
              <div className="absolute bottom-8 top-6 left-8 right-0 flex items-end justify-center overflow-hidden">
                {player.photoUrl ? (
                  <img src={player.photoUrl} alt={`${player.firstName}`} className="w-[105%] h-[105%] object-cover object-top" />
                ) : (
                  <div className="w-32 h-40 bg-black/10 rounded-t-full flex items-center justify-center mb-2">
                    <User className="w-20 h-20 text-white/30" strokeWidth={1.5} />
                  </div>
                )}
              </div>
              {/* Name bar */}
              <div className="absolute bottom-3 left-3 right-3 bg-black/30 backdrop-blur-sm rounded-lg p-1.5 z-10">
                <div className="text-white text-sm font-bold text-center truncate">{player.firstName} {player.lastName}</div>
              </div>
            </div>
          </div>

          {/* Player Info */}
          <div className="flex-1 min-w-0">
            <div className="mb-6">
              <div className="flex items-start justify-between">
                <h1 className="text-3xl lg:text-4xl font-bold text-white mb-2">{player.firstName} {player.lastName}</h1>
                <button onClick={openEditModal} className="text-white/60 hover:text-white transition-colors p-2 min-h-[44px] min-w-[44px] flex items-center justify-center">
                  <Settings className="w-5 h-5" />
                </button>
              </div>
              <div className="flex flex-wrap gap-4 text-white/80">
                <div className="flex items-center gap-2"><Shield className="w-4 h-4" /><span>{player.position || 'Not set'}</span></div>
                <div className="flex items-center gap-2"><Calendar className="w-4 h-4" /><span>Age {age}</span></div>
                <div className="flex items-center gap-2"><MapPin className="w-4 h-4" /><span>{getNameForNationality(player.nationality)}</span></div>
              </div>
            </div>
            <div className="flex gap-3 mb-6">
              <Link to={`/assess/${player.id}`} className="px-6 py-2.5 bg-white text-green-700 rounded-lg hover:bg-green-50 transition-colors text-sm font-medium flex items-center gap-2 min-h-[44px]">
                <Activity className="w-4 h-4" />New Assessment
              </Link>
              <Link to={`/reports/player/${player.id}`} className="px-6 py-2.5 bg-white/15 text-white rounded-lg hover:bg-white/25 transition-colors text-sm font-medium border border-white/30 min-h-[44px] flex items-center">
                View Report
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div className="bg-white/15 backdrop-blur-sm rounded-lg p-3 border border-white/25">
                <p className="text-xs text-white/70 mb-1">Date of Birth</p>
                <p className="font-bold text-white">{formatDate(player.dateOfBirth)}</p>
              </div>
              <div className="bg-white/15 backdrop-blur-sm rounded-lg p-3 border border-white/25">
                <p className="text-xs text-white/70 mb-1">Assessments</p>
                <p className="font-bold text-white">{assessments.length}</p>
              </div>
              <div className="bg-white/15 backdrop-blur-sm rounded-lg p-3 border border-white/25">
                <p className="text-xs text-white/70 mb-1">Overall Rating</p>
                <p className="font-bold text-yellow-300">{overallRating}/100</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Top Attributes */}
      {skillCategories.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <span className="text-yellow-500">⭐</span> Top Attributes
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {skillCategories.map((cat) => {
              const top = cat.skills.reduce((best, s) => s.score > best.score ? s : best, cat.skills[0]);
              if (!top) return null;
              const labelColor: Record<string, string> = { red: 'text-red-600', yellow: 'text-yellow-600', green: 'text-green-600', blue: 'text-blue-600' };
              return (
                <div key={cat.corner} className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                  <p className={`text-xs font-medium ${labelColor[cat.meta.color] || 'text-gray-500'} mb-1`}>{cat.meta.label}</p>
                  <p className="font-semibold text-gray-900">{top.name}</p>
                  <p className="text-green-600 font-bold text-lg">{top.score.toFixed(1)} <span className="text-xs text-gray-400 font-normal">/10</span></p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Development Focus Areas — powered by player-development-service */}
      <DevelopmentFocusAreas playerId={playerId} teamId={player.teamId} />

      {/* Summary */}
      {skillCategories.length > 0 && (() => {
        const summaryMatch = latestAssessment?.notes?.match(/\[COACH_SUMMARY\]\n([\s\S]*?)\n\[\/COACH_SUMMARY\]/);
        const summary = summaryMatch ? summaryMatch[1] : null;
        return (
          <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
            <h2 className="text-lg font-bold text-gray-900 mb-3">Summary</h2>
            {summary ? (
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                {summary.split('\n\n').map((para, i) => (
                  <p key={i} className="text-sm text-gray-700 leading-relaxed mb-3 last:mb-0">{para}</p>
                ))}
              </div>
            ) : (
              <div className="bg-gray-50 rounded-xl p-4 border border-dashed border-gray-200 text-center">
                <p className="text-sm text-gray-400">Assessment summary will appear here after the next assessment cycle.</p>
              </div>
            )}
          </div>
        );
      })()}

      {/* Player Attributes */}
      {skillCategories.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {skillCategories.map((cat) => {
            const bgMap: Record<string, string> = {
              red: 'bg-red-50 border-red-200',
              yellow: 'bg-yellow-50 border-yellow-200',
              green: 'bg-green-50 border-green-200',
              blue: 'bg-blue-50 border-blue-200',
            };
            const cardBg = bgMap[cat.meta.color] || 'bg-gray-50 border-gray-200';
            return (
              <div key={cat.corner} className={`rounded-2xl border p-6 ${cardBg}`}>
                <div className="flex flex-col items-center mb-5">
                  <CircularProgress value={cat.avg} color={cat.meta.color} size="lg" />
                  <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide mt-3">{cat.meta.label}</h3>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  {cat.skills.map((skill) => (
                    <CircularProgress key={skill.name} value={skill.score} max={10} label={skill.name.toUpperCase()} color={cat.meta.color} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Progress Over Time</h2>
          {progressData.length >= 2 ? (
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={progressData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="date" stroke="#6b7280" style={{ fontSize: '12px' }} />
                <YAxis domain={[0, 10]} stroke="#6b7280" style={{ fontSize: '12px' }} />
                <Tooltip />
                <Legend />
                {CORNERS.map((c) => (
                  <Line key={c} type="monotone" dataKey={c} stroke={CORNER_META[c].chartColor} strokeWidth={2} name={CORNER_META[c].label} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[250px] bg-gray-50 border-2 border-dashed border-gray-200 rounded-lg flex items-center justify-center">
              <p className="text-sm text-gray-400">Need at least 2 assessments to show progress</p>
            </div>
          )}
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Current Skills Assessment</h2>
          {radarData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <RadarChart data={radarData}>
                <PolarGrid stroke="#e5e7eb" />
                <PolarAngleAxis dataKey="skill" style={{ fontSize: '12px' }} />
                <PolarRadiusAxis domain={[0, 10]} style={{ fontSize: '12px' }} />
                <Radar name="Current Score" dataKey="value" stroke="#16a34a" fill="#16a34a" fillOpacity={0.3} />
              </RadarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[250px] bg-gray-50 border-2 border-dashed border-gray-200 rounded-lg flex items-center justify-center">
              <p className="text-sm text-gray-400">No assessments yet</p>
            </div>
          )}
        </div>
      </div>

      {/* Assessment History */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Assessment History ({assessments.length})</h2>
        {assessments.length === 0 ? (
          <div className="text-center py-8">
            <Activity className="w-10 h-10 mx-auto mb-2 text-gray-300" />
            <p className="text-sm text-gray-400 mb-3">No assessments recorded yet</p>
            <Link to={`/assess/${player.id}`} className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors min-h-[44px]">
              <Activity className="w-4 h-4" />Start First Assessment
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {[...assessments].reverse().map((a, idx) => {
              const tech = cornerAvg(a.scores.technical as Record<string, number | undefined>);
              const phys = cornerAvg(a.scores.physical as Record<string, number | undefined>);
              const psych = cornerAvg(a.scores.psychological as Record<string, number | undefined>);
              const soc = cornerAvg(a.scores.social as Record<string, number | undefined>);
              const overall = Math.round(((tech + phys + psych + soc) / 4) * 10);
              const isLatest = idx === 0;
              return (
                <div key={a.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-green-500" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{formatDate(a.assessmentDate)}</p>
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 capitalize">
                          {a.type.toLowerCase().replace('_', ' ')}
                        </span>
                        {a.emailSentAt ? (
                          <span className="text-xs text-gray-400 flex items-center gap-1">
                            📧 Emailed {new Date(a.emailSentAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })} {new Date(a.emailSentAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {isLatest && (
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          setSendingEmail(true);
                          api.post('/reports/send-assessment-email', { playerId: player.id })
                            .then(() => setSendingEmail(false))
                            .catch(() => setSendingEmail(false));
                        }}
                        disabled={sendingEmail}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors min-h-[36px] disabled:opacity-50"
                      >
                        <Send className="w-3 h-3" />
                        {sendingEmail ? 'Sending...' : a.emailSentAt ? 'Resend' : 'Send Report'}
                      </button>
                    )}
                    <div className="text-right">
                      <p className="text-sm font-semibold text-gray-900">{overall}/100</p>
                      <p className="text-xs text-gray-400">Overall</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Edit Player Modal */}
      {showEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowEditModal(false)} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <Edit className="w-5 h-5 text-green-600" />
                  </div>
                  <h2 className="text-lg font-semibold text-gray-900">Edit Player</h2>
                </div>
                <button onClick={() => setShowEditModal(false)} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 min-h-[44px] min-w-[44px] flex items-center justify-center" aria-label="Close">
                  <X className="w-5 h-5" />
                </button>
              </div>
              {editError && <Alert variant="destructive" className="mb-4"><AlertCircle className="h-4 w-4" /><AlertDescription>{editError}</AlertDescription></Alert>}

              {/* Tabs */}
              <div className="flex border-b border-gray-200 mb-4">
                <button onClick={() => setEditTab('details')}
                  className={`flex-1 pb-3 text-sm font-medium border-b-2 transition-colors ${editTab === 'details' ? 'border-green-600 text-green-700' : 'border-transparent text-gray-400 hover:text-gray-600'}`}>
                  Details
                </button>
                <button onClick={() => setEditTab('contacts')}
                  className={`flex-1 pb-3 text-sm font-medium border-b-2 transition-colors ${editTab === 'contacts' ? 'border-green-600 text-green-700' : 'border-transparent text-gray-400 hover:text-gray-600'}`}>
                  <span className="flex items-center justify-center gap-1.5"><Mail className="w-4 h-4" /> Contacts</span>
                </button>
              </div>

              {editTab === 'details' && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="editFirstName" className="text-sm font-medium text-gray-700">First Name <span className="text-red-500">*</span></Label>
                    <Input id="editFirstName" value={editFirstName} onChange={(e) => setEditFirstName(e.target.value)} className="mt-1.5" />
                  </div>
                  <div>
                    <Label htmlFor="editLastName" className="text-sm font-medium text-gray-700">Last Name <span className="text-red-500">*</span></Label>
                    <Input id="editLastName" value={editLastName} onChange={(e) => setEditLastName(e.target.value)} className="mt-1.5" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="editDob" className="text-sm font-medium text-gray-700">Date of Birth <span className="text-red-500">*</span></Label>
                  <Input id="editDob" type="date" value={editDob} onChange={(e) => setEditDob(e.target.value)} className="mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="editPosition" className="text-sm font-medium text-gray-700">Position <span className="text-red-500">*</span></Label>
                  <select id="editPosition" value={editPosition} onChange={(e) => setEditPosition(e.target.value)}
                    className="mt-1.5 w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm min-h-[44px] focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500">
                    <option value="">Select position</option>
                    <option value="Goalkeeper">Goalkeeper</option>
                    <option value="Defender">Defender</option>
                    <option value="Midfield">Midfield</option>
                    <option value="Winger">Winger</option>
                    <option value="Forward">Forward</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="editFan" className="text-sm font-medium text-gray-700">FAN Number <span className="text-gray-400">(optional)</span></Label>
                  <Input id="editFan" value={editFanNumber} onChange={(e) => setEditFanNumber(e.target.value)} className="mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="editNationality" className="text-sm font-medium text-gray-700">Nationality <span className="text-gray-400">(optional)</span></Label>
                  <select id="editNationality" value={editNationality} onChange={(e) => setEditNationality(e.target.value)}
                    className="mt-1.5 w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm min-h-[44px] focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500">
                    <option value="">Select nationality</option>
                    {NATIONALITIES.map((n) => (
                      <option key={n.code} value={n.code}>{n.flag} {n.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-700 mb-1.5 block">Player Photo <span className="text-gray-400">(optional)</span></Label>
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-20 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center overflow-hidden bg-gray-900 flex-shrink-0">
                      {editPhotoPreview || player.photoUrl ? (
                        <img src={editPhotoPreview || player.photoUrl} alt="Preview" className="w-full h-full object-contain" />
                      ) : (
                        <ImageIcon className="w-6 h-6 text-gray-500" />
                      )}
                    </div>
                    <div className="flex-1">
                      <label className={`inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors min-h-[44px] ${removingBg ? 'opacity-50 pointer-events-none' : 'cursor-pointer'}`}>
                        {removingBg ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                        {removingBg ? 'Processing...' : editPhotoFile ? 'Change Photo' : 'Upload Photo'}
                        <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" disabled={removingBg}
                          onChange={(e) => { const f = e.target.files?.[0]; if (f) handlePhotoSelect(f); }} />
                      </label>
                      {removingBg && (
                        <div className="mt-2">
                          <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                            <span>Removing background...</span>
                            <span>{bgProgress}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div className="bg-green-600 h-2 rounded-full transition-all duration-300" style={{ width: `${bgProgress}%` }} />
                          </div>
                        </div>
                      )}
                      {editPhotoFile && !removingBg && (
                        <p className="text-xs text-green-600 mt-1">Ready — save to apply</p>
                      )}
                      <p className="text-xs text-gray-400 mt-1">Background removed automatically</p>
                    </div>
                  </div>
                </div>
              </div>
              )}

              {/* Contacts Tab */}
              {editTab === 'contacts' && (
              <div className="space-y-4">
                {(() => {
                  const isUnder18 = editDob ? calculateAge(new Date(editDob)) < 18 : true;
                  return isUnder18 ? (
                    <>
                      <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
                        <p className="text-sm text-blue-800">This player is under 18. Add parent/guardian contact details to receive assessment reports.</p>
                      </div>
                      {editGuardians.map((g, i) => (
                        <div key={i} className="bg-gray-50 rounded-lg p-4 border border-gray-100 space-y-3">
                          <div className="flex items-center justify-between">
                            <p className="text-sm font-medium text-gray-700">Parent/Guardian {editGuardians.length > 1 ? i + 1 : ''}</p>
                            {editGuardians.length > 1 && (
                              <button onClick={() => setEditGuardians(prev => prev.filter((_, idx) => idx !== i))}
                                className="text-gray-400 hover:text-red-500 p-1 min-h-[44px] min-w-[44px] flex items-center justify-center">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <Label className="text-xs text-gray-500">First Name</Label>
                              <Input value={g.firstName} onChange={(e) => setEditGuardians(prev => prev.map((gd, idx) => idx === i ? { ...gd, firstName: e.target.value } : gd))}
                                placeholder="First name" className="mt-1" />
                            </div>
                            <div>
                              <Label className="text-xs text-gray-500">Last Name</Label>
                              <Input value={g.lastName} onChange={(e) => setEditGuardians(prev => prev.map((gd, idx) => idx === i ? { ...gd, lastName: e.target.value } : gd))}
                                placeholder="Last name" className="mt-1" />
                            </div>
                          </div>
                          <div>
                            <Label className="text-xs text-gray-500">Email Address</Label>
                            <Input type="email" value={g.email} onChange={(e) => setEditGuardians(prev => prev.map((gd, idx) => idx === i ? { ...gd, email: e.target.value } : gd))}
                              placeholder="parent@example.com" className="mt-1" />
                          </div>
                        </div>
                      ))}
                      {editGuardians.length === 0 && (
                        <div className="text-center py-4 text-sm text-gray-400">No parent/guardian contacts added yet.</div>
                      )}
                      <button onClick={() => setEditGuardians(prev => [...prev, { firstName: '', lastName: '', email: '' }])}
                        className="w-full border-2 border-dashed border-gray-300 rounded-lg py-3 text-sm text-gray-500 hover:border-green-400 hover:text-green-600 transition-colors flex items-center justify-center gap-2 min-h-[44px]">
                        <Plus className="w-4 h-4" /> Add {editGuardians.length > 0 ? 'Another ' : ''}Parent/Guardian
                      </button>
                    </>
                  ) : (
                    <>
                      <div className="bg-green-50 border border-green-100 rounded-lg p-3">
                        <p className="text-sm text-green-800">This player is 18 or older. Add their email to receive assessment reports directly.</p>
                      </div>
                      <div>
                        <Label htmlFor="editContactEmail" className="text-sm font-medium text-gray-700">Player Email</Label>
                        <Input id="editContactEmail" type="email" value={editContactEmail} onChange={(e) => setEditContactEmail(e.target.value)}
                          placeholder="player@example.com" className="mt-1.5" />
                      </div>
                    </>
                  );
                })()}
              </div>
              )}

              <div className="mt-6 pt-4 border-t border-gray-100">
                <div className="bg-red-50 border border-red-100 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-red-800 mb-2">Danger Zone</h3>
                  <p className="text-sm text-red-700 mb-3">Deleting this player will remove all associated assessments. This cannot be undone.</p>
                  <Button variant="destructive" size="sm" onClick={handleDelete} disabled={deleting} className="min-h-[44px]">
                    {deleting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Deleting...</> : <><Trash2 className="w-4 h-4 mr-2" />Delete Player</>}
                  </Button>
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-100">
                <Button variant="outline" onClick={() => setShowEditModal(false)} className="min-h-[44px]">Cancel</Button>
                <Button onClick={handleEditSave} disabled={editSaving} className="bg-green-600 hover:bg-green-700 text-white min-h-[44px]">
                  {editSaving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : <><Save className="w-4 h-4 mr-2" />Save Changes</>}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
